import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { Consent as Asked } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, Waiting } from '@beyond-js/ui/react';
import { Card, Heading, Outage, Problem, Wordmark, useArmed } from '@accounts/ui';

/**
 * The person's consent before an installed client on this computer is handed a product session:
 * `/consent?ticket=`, where `GET /v1/authorize` sends a hand-off to a loopback address (`127.0.0.1`).
 *
 * Any process on the computer can listen on a port and start that hand-off, so Accounts never sends it
 * a code by itself: this page names the product, the person it will act as and that an application on
 * this computer will hold that product's session, and only Continue issues the code. Cancel tells the
 * application the person refused (`error=access_denied`). Either way the page goes only to the address
 * the service answered, replacing itself so Back never decides again. The ticket stays in the address,
 * so a reload asks again; it is bound to this session and useless anywhere else. Inside another page's
 * frame nothing is offered to decide (and the static host refuses to be framed at `/consent`:
 * `public/_headers`), and Continue waits `ARMED` after the page is shown (`useArmed` in `@accounts/ui`,
 * shared with an application's Allow on `/connect`).
 */
export function Consent() {
	const { text, handoff, language } = usePresence();
	const [parameters] = useSearchParams();
	const ticket = parameters.get('ticket') ?? '';
	const [asked, setAsked] = useState<Asked>();
	const [unread, setUnread] = useState<Refusal>();
	const [failure, setFailure] = useState<unknown>();
	const [busy, setBusy] = useState<'continue' | 'cancel'>();
	const started = useRef(false);

	// Whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(async () => {
		setUnread(Refusal.kept);
		try {
			setAsked(await handoff.consent(ticket));
			setUnread(undefined);
			return true;
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return false;
			const given = Refusal.is(error) ? error : new Refusal('INTERNAL', String(error));
			setUnread(given);
			return !given.unreachable;
		}
	}, [handoff, ticket]);

	useEffect(() => {
		if (started.current) return;
		started.current = true;
		void read();
	}, [read]);

	async function decide(allowed: boolean) {
		setBusy(allowed ? 'continue' : 'cancel');
		setFailure(undefined);
		try {
			globalThis.location.replace(await handoff.decide(ticket, allowed));
		} catch (error) {
			setBusy(undefined);
			if (Refusal.is(error) && error.quiet) return;
			// A consent already decided, expired or of another session is said as gone, with no way to decide it
			if (Refusal.is(error) && error.code === 'EXCHANGE_INVALID') {
				setAsked(undefined);
				setUnread(error);
				return;
			}
			setFailure(error);
		}
	}

	// Inside another page's frame a press could be steered by what that page draws over this one, so the
	// decision is offered only in a window of its own
	const framed = globalThis.top !== globalThis.self;
	const armed = useArmed(Boolean(asked) && !framed);
	const reading = !asked && !unread;
	const gone = unread && !unread.unreachable;
	return (
		<div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-4 py-10">
			<Wordmark label="Accounts" />

			{reading ? <Waiting label={text('consent.reading')} language={language} kind="lines" rows={3} /> : null}
			{/* Until the request is read, or when it cannot be, the H1 names the place (D158) */}
			{unread ? <Heading title={text('place.consent')} /> : null}
			{/* The request is the page's one read: Accounts not answering it is the page's body (D163) */}
			<Outage whole reads={[{ key: 'request', title: text('outage.request'), unread, run: read }]} />
			{gone ? (
				<>
					<Callout tone="warning" live title={text('consent.gone')} />
					<Link to="/" className="self-start underline">
						{text('consent.onward')}
					</Link>
				</>
			) : null}

			{asked && framed ? <Callout tone="warning" live title={text('consent.framed')} /> : null}
			{asked && !framed ? (
				<>
					<Heading
						title={asked.person.name ? text('consent.title', { product: asked.product.name, person: asked.person.name }) : text('consent.title.unnamed', { product: asked.product.name })}
						lead={text('consent.lead', { product: asked.product.name })}
					/>
					<Card>
						<p className="text-body text-ink-muted">
							{text('consent.address', { address: asked.address })}
						</p>
						<div className="mt-4 flex flex-wrap gap-3">
							<Button variant="primary" busy={busy === 'continue'} disabled={busy !== undefined || !armed} labels={{ busy: text('consent.continuing') }} onClick={() => void decide(true)}>
								{text('consent.continue')}
							</Button>
							<Button variant="secondary" busy={busy === 'cancel'} disabled={busy !== undefined} onClick={() => void decide(false)}>
								{text('consent.cancel')}
							</Button>
						</div>
					</Card>
					{failure ? <Problem error={failure} /> : null}
				</>
			) : null}
		</div>
	);
}
